import { Printer, ExternalLink, FileText } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const PACK_URL = "https://nkbridalaustralia.com.au/print-pack";

const ITEMS = [
  {
    name: "Bridal A5 Flyer",
    path: "/pamphlet",
    format: "A5 · Double-sided",
    size: "148 × 210 mm",
    desc: "Two-page front & back flyer introducing NK Bridal's couture gowns.",
    testid: "pack-bridal-pamphlet",
  },
  {
    name: "Alterations A5 Flyer",
    path: "/alterations-pamphlet",
    format: "A5 · Double-sided",
    size: "148 × 210 mm",
    desc: "Two-page front & back flyer for the wedding alterations service.",
    testid: "pack-alterations-pamphlet",
  },
  {
    name: "Bridal Tri-fold Brochure",
    path: "/brochure",
    format: "DL Tri-fold · From A4",
    size: "210 × 99 mm panels",
    desc: "Six-panel folded brochure showcasing the collection and process.",
    testid: "pack-bridal-brochure",
  },
  {
    name: "Alterations Tri-fold Brochure",
    path: "/alterations-brochure",
    format: "DL Tri-fold · From A4",
    size: "210 × 99 mm panels",
    desc: "Six-panel folded brochure detailing alterations and pricing.",
    testid: "pack-alterations-brochure",
  },
  {
    name: "Business Card",
    path: "/business-card",
    format: "Front & Back",
    size: "90 × 54 mm",
    desc: "Nancy's contact card with logo, details and social handles.",
    testid: "pack-business-card",
  },
];

export default function PrintPackPage() {
  return (
    <div className="min-h-screen bg-ivory text-ink" data-testid="print-pack-page">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-16 md:py-24">
        {/* Header */}
        <header className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <img src="/nk-logo-dark.png" alt="NK Bridal" className="h-11 w-auto object-contain mb-8" />
            <p className="text-[11px] uppercase tracking-[0.35em] text-champagne mb-5">Print Pack</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl">
              Every print asset, in one place
            </h1>
            <p className="text-ink/60 font-light leading-relaxed max-w-xl mt-6 text-base">
              Open any item below, then use its <span className="italic">Print / Save PDF</span> button to
              download a print-ready PDF — or forward this page straight to your printer.
            </p>
          </div>

          {/* QR — scan to open on phone */}
          <div className="shrink-0 flex md:flex-col items-center gap-4 md:gap-3" data-testid="print-pack-qr">
            <div className="border border-line bg-white p-4">
              <QRCodeSVG value={PACK_URL} size={116} level="M" bgColor="#FFFFFF" fgColor="#0A0A0A" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-ink/50 md:text-center max-w-[130px] leading-relaxed">
              Scan to open on your phone
            </p>
          </div>
        </header>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
          {ITEMS.map((item) => (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noreferrer"
              data-testid={item.testid}
              className="group relative border border-line bg-white p-7 md:p-8 flex flex-col justify-between transition-all duration-500 hover:border-ink hover:-translate-y-1"
            >
              <div>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <FileText size={22} strokeWidth={1.2} className="text-ink/40 group-hover:text-ink transition-colors duration-500" />
                  <ExternalLink size={16} strokeWidth={1.4} className="text-ink/30 group-hover:text-champagne transition-colors duration-500" />
                </div>
                <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight tracking-tight">{item.name}</h2>
                <p className="text-ink/55 font-light leading-relaxed text-sm mt-3">{item.desc}</p>
              </div>
              <div className="mt-7 pt-5 border-t border-line flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink/50">
                  <span className="block">{item.format}</span>
                  <span className="block text-ink/35 mt-1">{item.size}</span>
                </div>
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink group-hover:text-champagne transition-colors duration-500">
                  <Printer size={14} strokeWidth={1.5} /> Open
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* Instructions */}
        <section className="mt-16 md:mt-20 border-t border-line pt-10" data-testid="print-pack-instructions">
          <p className="text-[11px] uppercase tracking-[0.3em] text-ink/50 mb-6">How to save a PDF</p>
          <ol className="grid sm:grid-cols-2 gap-x-12 gap-y-4 max-w-3xl text-sm font-light text-ink/70 leading-relaxed [counter-reset:step]">
            {[
              "Open an asset above (it launches in a new tab).",
              'Click the "Print / Save PDF" button at the top-right.',
              'Set Destination to "Save as PDF".',
              "Set Margins to None and enable Background graphics.",
              "Save — your print-ready PDF downloads instantly.",
              "Send the PDF to your printer, or print at home.",
            ].map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-serif text-champagne text-lg leading-none shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}
